import React, { useMemo, useState } from "react";
import { Compass, Sparkles } from "lucide-react";
import {
  AWARENESS_LEVELS,
  ANGLES,
  FUNNEL_STAGES,
  FORMATS,
} from "../services/theWebData";

/**
 * WebMatrixView — outline §10.3 "Gap Finder".
 *
 * 2D heatmap. Default axes: angle (rows) × awareness (columns).
 * Each cell aggregates the matching ads:
 *   - count
 *   - total spend
 *   - average ROAS
 *   - tested?  (≥1 ad with spend > kill threshold)
 *
 * Colour-coded by avg ROAS. Empty cells render as gap markers — those
 * are the briefs you should write next.
 *
 * Click a cell to drill in: a list panel below shows all matching ads,
 * each clickable to open the modal.
 */
const AXIS_OPTIONS = [
  { key: "awareness", label: "Awareness", values: AWARENESS_LEVELS },
  { key: "funnel",    label: "Funnel",    values: FUNNEL_STAGES },
  { key: "format",    label: "Format",    values: FORMATS.slice(0, 8) },
];
const ROW_OPTIONS = [
  { key: "angle", label: "Angle", values: ANGLES },
  { key: "format", label: "Format", values: FORMATS.slice(0, 8) },
];

export default function WebMatrixView({ nodes, onSelect }) {
  const [colKey, setColKey] = useState("awareness");
  const [rowKey, setRowKey] = useState("angle");
  const [drillCell, setDrillCell] = useState(null); // { col, row }

  const colAxis = AXIS_OPTIONS.find((a) => a.key === colKey) || AXIS_OPTIONS[0];
  const rowAxis = ROW_OPTIONS.find((a) => a.key === rowKey) || ROW_OPTIONS[0];

  const matrix = useMemo(() => {
    const cells = new Map();
    const ads = nodes.filter(
      (n) => n.type === "concept" || n.type === "variation",
    );
    rowAxis.values.forEach((r) => {
      colAxis.values.forEach((c) => {
        const key = `${r.key}|${c.key}`;
        cells.set(key, {
          row: r,
          col: c,
          items: [],
          spend: 0,
          roas: 0,
          count: 0,
        });
      });
    });
    ads.forEach((n) => {
      const r = n[rowAxis.key];
      const c = n[colAxis.key];
      if (!r || !c) return;
      const key = `${r}|${c}`;
      const cell = cells.get(key);
      if (!cell) return;
      cell.items.push(n);
      cell.spend += n.spend || 0;
      cell.roas += (n.roas || 0) * Math.max(1, n.spend || 1);
      cell.count += 1;
    });
    cells.forEach((cell) => {
      const totalSpendW = cell.items.reduce(
        (acc, n) => acc + Math.max(1, n.spend || 1),
        0,
      );
      cell.avgRoas = totalSpendW > 0 ? cell.roas / totalSpendW : 0;
    });
    return cells;
  }, [nodes, rowAxis, colAxis]);

  const drillItems = useMemo(() => {
    if (!drillCell) return [];
    const key = `${drillCell.row}|${drillCell.col}`;
    return matrix.get(key)?.items || [];
  }, [drillCell, matrix]);

  return (
    <div className="web-matrix-wrap">
      <div className="web-matrix-toolbar">
        <span className="web-matrix-toolbar__label">Rows</span>
        {ROW_OPTIONS.map((o) => (
          <button
            key={o.key}
            className="web-chip"
            data-active={rowKey === o.key}
            onClick={() => setRowKey(o.key)}
          >
            {o.label}
          </button>
        ))}
        <span className="web-toolbar__divider" />
        <span className="web-matrix-toolbar__label">Columns</span>
        {AXIS_OPTIONS.map((o) => (
          <button
            key={o.key}
            className="web-chip"
            data-active={colKey === o.key}
            onClick={() => setColKey(o.key)}
          >
            {o.label}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--w-muted)" }}>
          Empty cells = gaps. Filled cells colour-coded by avg ROAS.
        </span>
      </div>

      <div
        className="web-matrix"
        style={{
          gridTemplateColumns: `180px repeat(${colAxis.values.length}, minmax(0, 1fr))`,
        }}
      >
        {/* corner cell */}
        <div className="web-matrix__corner">
          <span style={{ color: "var(--w-muted)" }}>{rowAxis.label}</span>
          <span className="web-matrix__corner-arrow">↓ ↦</span>
          <span style={{ color: "var(--w-muted)" }}>{colAxis.label}</span>
        </div>

        {/* column headers */}
        {colAxis.values.map((c) => (
          <div key={`h-${c.key}`} className="web-matrix__col-head">
            {c.label}
          </div>
        ))}

        {/* rows */}
        {rowAxis.values.map((r) => (
          <React.Fragment key={`r-${r.key}`}>
            <div className="web-matrix__row-head">{r.label}</div>
            {colAxis.values.map((c) => {
              const key = `${r.key}|${c.key}`;
              const cell = matrix.get(key);
              const isActive =
                drillCell?.row === r.key && drillCell?.col === c.key;
              return (
                <button
                  type="button"
                  key={`c-${key}`}
                  className={`web-matrix__cell ${isActive ? "is-active" : ""} ${
                    cell.count === 0 ? "is-gap" : ""
                  }`}
                  data-tier={
                    cell.avgRoas >= 4 ? "gold" :
                    cell.avgRoas >= 3 ? "silver" :
                    cell.avgRoas >= 2 ? "bronze" :
                    cell.count > 0 ? "low" : "gap"
                  }
                  onClick={() => {
                    if (cell.count === 0) {
                      setDrillCell({ row: r.key, col: c.key, gap: true });
                    } else {
                      setDrillCell({ row: r.key, col: c.key });
                    }
                  }}
                >
                  {cell.count > 0 ? (
                    <>
                      <div className="web-matrix__cell-roas">
                        {cell.avgRoas.toFixed(1)}×
                      </div>
                      <div className="web-matrix__cell-meta">
                        {cell.count} · ${shortMoney(cell.spend)}
                      </div>
                    </>
                  ) : (
                    <div className="web-matrix__cell-gap">
                      <Sparkles size={11} /> gap
                    </div>
                  )}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      {drillCell ? (
        <DrillPanel
          drill={drillCell}
          items={drillItems}
          onClose={() => setDrillCell(null)}
          onSelect={onSelect}
        />
      ) : null}
    </div>
  );
}

const DrillPanel = ({ drill, items, onClose, onSelect }) => {
  const isGap = items.length === 0;
  return (
    <div className="web-matrix__drill">
      <div className="web-matrix__drill-head">
        <div>
          <div className="web-modal__section-label">
            {isGap ? "Untested intersection" : `${items.length} ads`} · {drill.row} × {drill.col}
          </div>
          {isGap ? (
            <p style={{ margin: 0, fontSize: 13, color: "var(--w-muted)" }}>
              You've never tested this combination. Generate a brief to fill the gap.
            </p>
          ) : null}
        </div>
        <button className="web-control-btn" onClick={onClose}>×</button>
      </div>

      {isGap ? (
        <button className="web-btn web-btn--gold" style={{ marginTop: 12, alignSelf: "flex-start" }}>
          <Compass size={14} /> Generate gap brief
        </button>
      ) : (
        <div className="web-matrix__drill-list">
          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              className="web-rel__item"
              onClick={() => onSelect(n.id)}
            >
              <span className="web-rel__type">{(n.status || "active").slice(0, 4).toUpperCase()}</span>
              <span className="web-rel__name">{n.label}</span>
              <span className="web-rel__metric">
                {(n.roas || 0).toFixed(1)}× · ${shortMoney(n.spend || 0)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function shortMoney(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return Math.round(n).toString();
}
